import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Shield, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";
import { getAuthHintCookie } from "@/lib/auth-hint-cookie";

const logoPublic = "/lovable-uploads/d4167bdc-fee8-46ee-82fb-52c880b10d47.webp";
const logoCompact = "/lovable-uploads/fee502b0-3802-425b-996a-f917aa689b10.webp";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hintedUserType, setHintedUserType] = useState<"humano" | "compawdre" | null>(null);
  const { user, profile, isAdmin, isSignedIn, loading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { setType, clearType } = useUserType();

  const isHydrationStableSignedIn = !loading && isSignedIn;
  const userType = profile?.user_type ?? hintedUserType;

  const handleLogoutClick = () => {
    setIsMobileMenuOpen(false);
    navigate("/confirmar-salida", { state: { from: location.pathname } });
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setHintedUserType(getAuthHintCookie()?.userType ?? null);
  }, []);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };



  const editProfilePath = userType === "compawdre"
    ? "/dashboard-compawdre/perfil"
    : "/dashboard-humano/perfil";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-soft">
      <div className="container-custom mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-[70px]">
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img
              alt="Compaws"
              width={160}
              height={56}
              className="h-10 lg:h-14 w-auto object-contain"
              src={isHydrationStableSignedIn ? logoCompact : logoPublic}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-6 whitespace-nowrap">
            {!isHydrationStableSignedIn && (
              <>
                <button onClick={() => scrollToSection("servicios")} className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Servicios
                </button>
                <button onClick={() => scrollToSection("como-funciona")} className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Cómo funciona
                </button>
                <Link to="/registro" onClick={() => setType("compawdre")} className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Soy cuidador
                </Link>
                <button onClick={() => scrollToSection("ayuda")} className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Ayuda
                </button>
                <Link to="/buscar" className="font-fredoka font-semibold py-2 px-4 text-sm rounded-lg text-white bg-primary hover:bg-accent hover:text-foreground transition-colors">
                  Buscar Compawdre
                </Link>
              </>
            )}

            {isHydrationStableSignedIn && !isAdmin && (
              <>
                <Link to="/" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Inicio
                </Link>
                <Link to="/dashboard" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Dashboard
                </Link>
                <Link to="/buscar" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Buscar
                </Link>
                {userType === "compawdre" && (
                  <Link to="/mi-perfil" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                    Mi perfil público
                  </Link>
                )}
              </>
            )}

            {isHydrationStableSignedIn && isAdmin && (
              <>
                <Link to="/admin" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Dashboard
                </Link>
                <Link to="/dashboard-humano/perfil" className="font-poppins text-sm text-foreground/80 hover:text-destructive transition-colors">
                  Mi perfil
                </Link>
              </>
            )}
          </div>

          {/* Desktop right area */}
          <div className="hidden lg:flex items-center gap-3 whitespace-nowrap flex-shrink-0">
            {!isHydrationStableSignedIn && (
              <>
                <Link
                  to="/login"
                  className="font-fredoka font-semibold py-2 px-5 text-sm rounded-xl border-2 border-foreground text-foreground bg-white hover:bg-destructive hover:border-destructive hover:text-white transition-colors"
                >
                  Iniciar Sesión
                </Link>
                <Link
                  to="/registro"
                  onClick={() => clearType()}
                  className="font-fredoka font-semibold py-2 px-5 text-sm rounded-xl border-2 border-foreground text-foreground bg-white hover:bg-destructive hover:border-destructive hover:text-white transition-colors"
                >
                  Registrarse
                </Link>
              </>
            )}

            {isHydrationStableSignedIn && isAdmin && (
              <div className="flex items-center gap-2 bg-destructive/10 text-destructive px-3 py-1 rounded-full">
                <Shield size={16} />
                <span className="font-fredoka text-sm">Admin</span>
              </div>
            )}

            {isHydrationStableSignedIn && !isAdmin && (
              <Link
                to={editProfilePath}
                className="font-fredoka font-semibold py-2 px-4 text-sm rounded-lg text-white bg-primary hover:bg-accent hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <Pencil size={14} />
                Editar perfil
              </Link>
            )}

            {isHydrationStableSignedIn && (
              <button
                onClick={handleLogoutClick}
                className="p-2.5 rounded-xl border-2 border-foreground text-foreground hover:bg-destructive hover:border-destructive hover:text-white transition-colors"
                aria-label="Cerrar sesión"
              >
                <LogOut size={18} />
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border py-4 px-4 animate-fade-in">
            <div className="flex flex-col gap-3">
              {!isHydrationStableSignedIn && (
                <>
                  <Link to="/buscar" onClick={() => setIsMobileMenuOpen(false)} className="font-fredoka font-semibold text-center py-3 rounded-lg text-white bg-primary">
                    Buscar Compawdre
                  </Link>
                  <button onClick={() => scrollToSection("servicios")} className="font-poppins text-sm text-foreground/80 hover:text-destructive text-left py-2">
                    Servicios
                  </button>
                  <button onClick={() => scrollToSection("como-funciona")} className="font-poppins text-sm text-foreground/80 hover:text-destructive text-left py-2">
                    Cómo funciona
                  </button>
                  <Link to="/registro" onClick={() => { setType("compawdre"); setIsMobileMenuOpen(false); }} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Soy cuidador
                  </Link>
                  <button onClick={() => scrollToSection("ayuda")} className="font-poppins text-sm text-foreground/80 hover:text-destructive text-left py-2">
                    Ayuda
                  </button>
                  <div className="border-t border-border pt-3 flex flex-col gap-3">
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="font-fredoka font-semibold text-center py-3 rounded-xl border-2 border-foreground text-foreground">
                      Iniciar Sesión
                    </Link>
                    <Link to="/registro" onClick={() => { clearType(); setIsMobileMenuOpen(false); }} className="font-fredoka font-semibold text-center py-3 rounded-xl border-2 border-foreground text-foreground">
                      Registrarse
                    </Link>
                  </div>
                </>
              )}

              {isHydrationStableSignedIn && !isAdmin && (
                <>
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Inicio
                  </Link>
                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Dashboard
                  </Link>
                  <Link to="/buscar" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Buscar
                  </Link>
                  {userType === "compawdre" && (
                    <Link to="/mi-perfil" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                      Mi perfil público
                    </Link>
                  )}
                  <div className="border-t border-border pt-3 flex flex-col gap-3">
                    <Link to={editProfilePath} onClick={() => setIsMobileMenuOpen(false)} className="font-fredoka font-semibold text-center py-3 rounded-lg text-white bg-primary flex items-center justify-center gap-2">
                      <Pencil size={14} /> Editar perfil
                    </Link>
                    <button onClick={handleLogoutClick} className="font-fredoka font-semibold text-center py-3 rounded-xl border-2 border-foreground text-foreground hover:bg-destructive hover:border-destructive hover:text-white transition-colors flex items-center justify-center gap-2">
                      <LogOut size={18} /> Cerrar Sesión
                    </button>
                  </div>
                </>
              )}

              {isHydrationStableSignedIn && isAdmin && (
                <>
                  <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Dashboard
                  </Link>
                  <Link to="/dashboard-humano/perfil" onClick={() => setIsMobileMenuOpen(false)} className="font-poppins text-sm text-foreground/80 hover:text-destructive py-2">
                    Mi perfil
                  </Link>
                  <div className="border-t border-border pt-3 flex flex-col gap-3">
                    <button onClick={handleLogoutClick} className="font-fredoka font-semibold text-center py-3 rounded-xl border-2 border-foreground text-foreground hover:bg-destructive hover:border-destructive hover:text-white transition-colors flex items-center justify-center gap-2">
                      <LogOut size={18} /> Cerrar Sesión
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
